'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import Link from 'next/link';
import Script from 'next/script';
import { statusLabel, formatOrderNo } from '@/lib/orderStatus';

declare global {
  interface Window {
    YT?: {
      Player: new (
        elId: string,
        opts: { events: { onReady: (e: { target: { playVideo: () => void; mute: () => void; unMute: () => void } }) => void } }
      ) => { unMute: () => void; mute: () => void };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

const COLORS = {
  bg: '#f3f2f2',
  surface: '#eae9e9',
  pageBg: '#000000',
  text: '#201e1d',
  accent: '#ec3013',
  accent100: '#fff2ef',
  accent300: '#ffc4b8',
  accent600: '#dd2b0f',
  accent700: '#ae1800',
  divider: 'rgba(32,30,29,.4)',
  neutral500: '#9b9797',
  neutral900: '#2d2b2b',
};

interface ProductOption {
  id: string;
  option_label: string;
  price: number;
  list_price: number | null;
  stock: number;
}

interface Product {
  id: string;
  name: string;
  price: number;
  list_price: number | null;
  image_url: string | null;
  stock: number;
  has_options?: boolean;
  adult_only?: boolean;
  options?: ProductOption[];
}

/** has_options 상품은 기본 행 자체엔 재고가 없다 — 옵션 재고 합계를 실제 판매가능 재고로 취급. */
function effectiveStock(p: Product): number {
  return p.has_options ? (p.options ?? []).reduce((s, o) => s + o.stock, 0) : p.stock;
}

function priceLabel(p: Product): string {
  if (!p.has_options) return fmtWon(p.price);
  const prices = (p.options ?? []).map(o => o.price);
  if (prices.length === 0) return '-';
  const min = Math.min(...prices), max = Math.max(...prices);
  return min === max ? fmtWon(min) : `${fmtWon(min)}~`;
}

interface OrderSummary {
  order_no: string;
  status: string;
  total_amount: number;
  created_at: string;
  items: { product_name: string; qty: number }[];
}

interface ChatMsg {
  id: number;
  author: string;
  text: string;
}

interface Props {
  brandName: string;
  refCode: string;
  embedSrc: string | null;
  products: Product[];
  orders: OrderSummary[];
  buyerLoggedIn: boolean;
  adultOnly: boolean;
}

function fmtWon(n: number) {
  return n.toLocaleString('ko-KR') + '원';
}

export default function LiveClient({ brandName, refCode, embedSrc, products, orders, buyerLoggedIn, adultOnly }: Props) {
  const [viewerCount, setViewerCount] = useState(128);
  const [muted, setMuted] = useState(true);
  const [showUnmuteOverlay, setShowUnmuteOverlay] = useState(true);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [showStore, setShowStore] = useState(false);
  const [showPurchase, setShowPurchase] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | undefined>(undefined);
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const chatListRef = useRef<HTMLDivElement | null>(null);
  const lastMsgIdRef = useRef(0);
  const playerRef = useRef<{ unMute: () => void; mute: () => void } | null>(null);
  const playerReadyRef = useRef(false);
  const pendingUnmuteRef = useRef(false);

  // ---- 시청자수: 실제 동시접속 집계 인프라 없이 분위기용으로 완만히 변동 ----
  useEffect(() => {
    const t = setInterval(() => {
      setViewerCount(n => Math.max(50, n + Math.floor(Math.random() * 11) - 5));
    }, 4000);
    return () => clearInterval(t);
  }, []);

  // ---- 유튜브 IFrame API: 무음 자동재생으로 시작, 버튼 클릭 시 실제 언뮤트 ----
  // 스크립트 자체는 아래 <Script>가 로드하고, 여기서는 준비 콜백만 등록한다.
  useEffect(() => {
    if (!embedSrc) return;
    window.onYouTubeIframeAPIReady = () => {
      if (!window.YT) return;
      new window.YT.Player('live-player', {
        events: {
          onReady: (e) => {
            playerReadyRef.current = true;
            playerRef.current = e.target;
            e.target.playVideo();
            if (pendingUnmuteRef.current) e.target.unMute();
          },
        },
      });
    };
    // 스크립트가 이미 로드되어 API가 준비돼 있었다면(재마운트 등) 직접 호출.
    if (window.YT) window.onYouTubeIframeAPIReady();
  }, [embedSrc]);

  const toggleMute = () => {
    setMuted(m => {
      const next = !m;
      if (next) {
        pendingUnmuteRef.current = false;
        if (playerReadyRef.current) playerRef.current?.mute();
      } else {
        pendingUnmuteRef.current = true;
        if (playerReadyRef.current) playerRef.current?.unMute();
        setShowUnmuteOverlay(false); // 소리가 켜지면 중앙 버튼은 더 필요 없다.
      }
      return next;
    });
  };

  // ---- 채팅 폴링 ----
  const ref_ = useRef({ polling: false });
  const pollChat = useCallback(async () => {
    if (ref_.current.polling) return;
    ref_.current.polling = true;
    try {
      const url = lastMsgIdRef.current
        ? `/api/live/chat?ref=${encodeURIComponent(refCode)}&afterId=${lastMsgIdRef.current}`
        : `/api/live/chat?ref=${encodeURIComponent(refCode)}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json() as { messages: ChatMsg[] };
        if (data.messages.length > 0) {
          setMessages(prev => [...prev, ...data.messages].slice(-100));
          lastMsgIdRef.current = data.messages[data.messages.length - 1].id;
        }
      }
    } finally {
      ref_.current.polling = false;
    }
  }, [refCode]);

  useEffect(() => {
    pollChat();
    const t = setInterval(pollChat, 2500);
    return () => clearInterval(t);
  }, [pollChat]);

  useEffect(() => {
    const el = chatListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const requireLogin = () => {
    alert('로그인이 필요합니다.');
    window.location.href = `/login?redirect=${encodeURIComponent(`/live/${refCode}`)}`;
  };

  const sendChat = async () => {
    if (!buyerLoggedIn) { requireLogin(); return; }
    const text = chatInput.trim();
    if (!text) return;
    setChatInput('');
    const res = await fetch('/api/live/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ref: refCode, text }),
    });
    if (res.ok) pollChat();
  };

  const featuredProduct = products.find(p => effectiveStock(p) > 0) ?? products[0] ?? null;

  const openPurchase = (p: Product) => {
    if (!buyerLoggedIn) { requireLogin(); return; }
    setSelectedProduct(p);
    const firstAvailable = (p.options ?? []).find(o => o.stock > 0) ?? p.options?.[0];
    setSelectedOptionId(p.has_options ? firstAvailable?.id : undefined);
    setQuantity(1);
    setShowPurchase(true);
    setShowStore(false);
  };

  const selectedOption = selectedProduct?.has_options
    ? (selectedProduct.options ?? []).find(o => o.id === selectedOptionId)
    : undefined;
  const unitPrice = selectedProduct
    ? (selectedProduct.has_options ? (selectedOption?.price ?? 0) : selectedProduct.price)
    : 0;
  const total = unitPrice * quantity;

  // 구매하기 → 이 채널(refCode)의 장바구니에 담고 장바구니 페이지로 이동 (즉시결제가 아님).
  const submitPurchase = async () => {
    if (!selectedProduct || submitting) return;
    const productId = selectedProduct.has_options ? selectedOptionId : selectedProduct.id;
    if (!productId) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId, action: 'add', qty: quantity, ref: refCode }),
      });
      const data = await res.json();
      if (data.ok) {
        window.location.href = `/cart/${refCode}`;
      } else {
        setSubmitting(false);
        if (data.requireLogin) requireLogin();
        else if (data.error) alert(data.error);
      }
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <div className="live-page-outer" style={{ height: '100dvh', maxHeight: '100dvh', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', background: COLORS.pageBg, fontFamily: '-apple-system, "Pretendard", "Malgun Gothic", system-ui, sans-serif', color: COLORS.text, overflow: 'hidden' }}>
      <style>{`
        @keyframes livePulse { 0%,100%{opacity:1} 50%{opacity:.4} }
        html, body { overflow: hidden !important; height: 100% !important; height: 100dvh !important; margin: 0 !important; padding: 0 !important; }
        @media (max-width: 520px) {
          .live-page-outer { padding: 0 !important; }
          .live-page-card { border: none !important; max-width: 100% !important; width: 100% !important; height: 100dvh !important; }
        }
      `}</style>
      <div className="live-page-card" style={{ position: 'relative', width: '100%', maxWidth: 480, height: '100dvh', maxHeight: '100dvh', background: COLORS.bg, overflow: 'hidden' }}>

        <div style={{ position: 'relative', width: '100%', height: '100%', background: COLORS.neutral900, overflow: 'hidden' }}>
          {embedSrc ? (
            <iframe
              id="live-player"
              src={embedSrc}
              title="live"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', pointerEvents: 'none' }}
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          ) : (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.neutral500, fontFamily: 'monospace', fontSize: 12, textAlign: 'center', padding: 20 }}>
              방송 준비 중입니다.<br />곧 시작할 예정이니 잠시만 기다려주세요.
            </div>
          )}

          {/* 무음해제 버튼: 로딩 직후 화면 중앙에 노출, 클릭 시 소리 켜고 사라짐 */}
          {showUnmuteOverlay && muted && embedSrc && (
            <button
              onClick={(e) => { e.stopPropagation(); toggleMute(); }}
              style={{
                position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 15,
                width: 120, height: 120, display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 8, background: 'rgba(0,0,0,.6)', border: '1px solid rgba(255,255,255,.25)',
                color: '#fff', fontSize: 15, fontWeight: 700, borderRadius: 20, cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: 40, lineHeight: 1 }}>🔇</span>
              <span>소리 켜기</span>
            </button>
          )}

          {/* 상단 오버레이 */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, padding: '12px 14px 40px', background: 'linear-gradient(180deg, rgba(0,0,0,.7), rgba(0,0,0,0))' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ color: '#fff', fontWeight: 400, fontSize: 15 }}>{brandName}<span style={{ opacity: .7, fontSize: 12, marginLeft: 4 }}>({refCode})</span></div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: COLORS.accent, color: '#fff', fontSize: 11, padding: '3px 10px', borderRadius: 0 }}>
                <span style={{ width: 6, height: 6, background: '#fff', borderRadius: '50%', animation: 'livePulse 1.4s infinite' }} />
                LIVE
              </div>
              {adultOnly && (
                <div style={{ display: 'inline-flex', alignItems: 'center', background: '#dc2626', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 9999 }}>
                  19
                </div>
              )}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,.18)', color: '#fff', fontSize: 11, padding: '3px 10px', borderRadius: 0 }}>
                <span>👁</span>{viewerCount.toLocaleString('ko-KR')}
              </div>
              <div style={{ flex: 1 }} />
              <Link href={`/${refCode}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(0,0,0,.45)', color: '#fff', fontSize: 11, fontWeight: 600, padding: '0 12px', height: 38, borderRadius: 19, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                🏠 채널 메인
              </Link>
              <button onClick={toggleMute} style={{ width: 38, height: 38, flexShrink: 0, border: 'none', background: 'rgba(0,0,0,.45)', color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}>
                {muted ? '🔇' : '🔊'}
              </button>
            </div>
            <div style={{ color: '#fff', fontSize: 17, lineHeight: 1.3, marginTop: 12 }}>{brandName}의 라이브 방송에 오신 것을 환영합니다</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, background: 'rgba(0,0,0,.4)', padding: '7px 14px', borderRadius: 20, width: '100%' }}>
              <span style={{ fontSize: 12 }}>🔔</span>
              <span style={{ color: '#fff', fontSize: 12 }}>궁금한 점은 채팅으로 편하게 문의해주세요!</span>
            </div>
          </div>

          {/* 상품/채팅 영역 */}
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 'calc(52px + env(safe-area-inset-bottom, 0px))', maxHeight: '50%', overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 0, zIndex: 10 }}>
            <div ref={chatListRef} style={{ maxHeight: 170, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 6, padding: '6px 6px 8px', background: 'linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,.55) 40%)' }}>
              {/* 높이 추정이 아니라 개수로 정확히 5줄만 화면에 유지 (전체 이력은 messages 상태에 남아있음) */}
              {messages.slice(-5).map(msg => (
                <div key={msg.id} style={{ fontSize: 12.5, lineHeight: 1.4, color: '#fff' }}>
                  <span style={{ fontWeight: 400, color: COLORS.accent300 }}>{msg.author}</span>
                  <span style={{ opacity: .92 }}> : {msg.text}</span>
                </div>
              ))}
            </div>
            {featuredProduct && (
              <div style={{ margin: '2px 5px', padding: '8px 10px 8px 8px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, background: 'rgba(0,0,0,.65)', border: `1px solid rgba(255,255,255,.1)`, borderRadius: 12, backdropFilter: 'blur(6px)' }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, background: '#ddd', border: `1px solid ${COLORS.divider}`, overflow: 'hidden' }}>
                  {featuredProduct.image_url && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={featuredProduct.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {featuredProduct.adult_only && <span style={{ fontSize: 10, fontWeight: 700, background: '#dc2626', color: '#fff', borderRadius: 3, padding: '0 4px', marginRight: 4 }}>19</span>}
                    {featuredProduct.name}
                  </div>
                  <div style={{ fontSize: 13.5, marginTop: 2 }}>
                    {!featuredProduct.has_options && featuredProduct.list_price != null && featuredProduct.list_price > featuredProduct.price && (
                      <span style={{ color: 'rgba(255,255,255,.5)', textDecoration: 'line-through', marginRight: 6, fontSize: 12 }}>{fmtWon(featuredProduct.list_price)}</span>
                    )}
                    <span style={{ color: COLORS.accent300, fontWeight: 700 }}>{priceLabel(featuredProduct)}</span>
                  </div>
                </div>
                <button onClick={() => openPurchase(featuredProduct)} style={{ flexShrink: 0, background: COLORS.accent, color: '#fff', border: 'none', padding: '7px 16px', fontSize: 13, borderRadius: 12, cursor: 'pointer' }}>구매</button>
              </div>
            )}
          </div>

          {/* 하단 바 */}
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 20, padding: '8px 10px calc(8px + env(safe-area-inset-bottom, 0px))', background: 'rgba(0,0,0,.75)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={() => setShowStore(true)} style={{ flexShrink: 0, width: 34, height: 34, padding: 0, borderRadius: '50%', background: COLORS.accent, color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l1.5-5h15L21 9" /><path d="M4 9v11h16V9" /><path d="M4 9a2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0" /><path d="M10 20v-6h4v6" /></svg>
            </button>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(60,60,60,.55)', borderRadius: 17, padding: '0 6px 0 0' }}>
              <input
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => {
                  // 한글 IME 조합 중 Enter는 글자 확정용이라, 조합이 끝난 뒤의 Enter에만 전송한다.
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) sendChat();
                }}
                placeholder="메시지를 입력해주세요."
                style={{ flex: 1, minWidth: 0, height: 34, background: 'transparent', border: 'none', color: '#fff', padding: '0 12px', fontSize: 14 }}
              />
              <button onClick={sendChat} style={{ width: 28, height: 28, flexShrink: 0, background: COLORS.accent, color: COLORS.bg, borderRadius: '50%', border: 'none', cursor: 'pointer' }}>➤</button>
            </div>
            <button onClick={() => setShowOrders(true)} style={{ flexShrink: 0, width: 34, height: 34, padding: 0, background: 'rgba(255,255,255,.1)', color: '#fff', border: '1px solid rgba(255,255,255,.3)', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M7 3h10a1 1 0 0 1 1 1v17l-3-2-3 2-3-2-3 2V4a1 1 0 0 1 1-1Z" /><path d="M9 8h6M9 12h6" /></svg>
            </button>
          </div>
        </div>

        {/* 주문내역 바텀시트 */}
        {showOrders && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(45,43,43,.5)', zIndex: 65 }}>
            <div style={{ width: '100%', maxHeight: '78%', overflow: 'hidden', background: COLORS.surface, borderRadius: '20px 20px 0 0', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: `2px solid ${COLORS.divider}` }}>
                <div style={{ fontSize: 16 }}>주문내역</div>
                <div style={{ flex: 1 }} />
                <button onClick={() => setShowOrders(false)} style={{ width: 28, height: 28, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16 }}>✕</button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 12px' }}>
                {orders.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', fontSize: 12.5, color: 'rgba(32,30,29,.55)' }}>구매한 상품이 없어요</div>
                ) : orders.map(o => (
                  <div key={o.order_no} style={{ padding: '10px 0', borderBottom: `1px solid ${COLORS.divider}` }}>
                    <div style={{ fontSize: 11, color: 'rgba(32,30,29,.55)' }}>{o.created_at.slice(0, 10)} · {formatOrderNo(o.order_no)}</div>
                    <div style={{ fontSize: 13, margin: '3px 0' }}>
                      {o.items[0]?.product_name ?? '-'}{o.items.length > 1 ? ` 외 ${o.items.length - 1}건` : ''}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: 12, color: 'rgba(32,30,29,.55)' }}>{fmtWon(o.total_amount)}</div>
                      <div style={{ fontSize: 12, color: COLORS.accent700 }}>{statusLabel(o.status)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 상품 목록 다이얼로그 */}
      {showStore && (
        <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(45,43,43,.5)', zIndex: 60 }}>
          <div style={{ width: 'min(440px, 100%)', maxHeight: '82vh', overflow: 'hidden', background: COLORS.surface, boxShadow: '0 12px 32px rgba(45,43,43,.22)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', padding: 16, borderBottom: `2px solid ${COLORS.divider}` }}>
              <div style={{ fontSize: 20, fontWeight: 400 }}>판매 상품 {products.length}개</div>
              <div style={{ flex: 1 }} />
              <button onClick={() => setShowStore(false)} style={{ width: 28, height: 28, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16 }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 16px' }}>
              {products.map(p => {
                const soldOut = effectiveStock(p) <= 0;
                return (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: `1px solid ${COLORS.divider}` }}>
                    <div style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, background: '#ddd', border: `1px solid ${COLORS.divider}`, overflow: 'hidden' }}>
                      {p.image_url && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={p.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, lineHeight: 1.35 }}>
                        {p.adult_only && <span style={{ fontSize: 10, fontWeight: 700, background: '#dc2626', color: '#fff', borderRadius: 3, padding: '0 4px', marginRight: 4 }}>19</span>}
                        {p.name}{p.has_options && <span style={{ marginLeft: 6, fontSize: 11, color: COLORS.neutral500 }}>옵션 {p.options?.length ?? 0}종</span>}
                      </div>
                      <div style={{ fontSize: 14, marginTop: 4 }}>
                        {!p.has_options && p.list_price != null && p.list_price > p.price && (
                          <span style={{ color: COLORS.neutral500, textDecoration: 'line-through', marginRight: 6, fontSize: 12.5 }}>{fmtWon(p.list_price)}</span>
                        )}
                        <span style={{ color: COLORS.accent700 }}>{priceLabel(p)}</span>
                      </div>
                    </div>
                    {soldOut ? (
                      <div style={{ flexShrink: 0, fontSize: 11, padding: '3px 10px', borderRadius: 14, background: '#f8f4f4' }}>품절</div>
                    ) : (
                      <button onClick={() => openPurchase(p)} style={{ flexShrink: 0, borderRadius: 14, background: COLORS.accent, color: '#fff', border: 'none', padding: '8px 14px', fontSize: 14, cursor: 'pointer' }}>구매하기</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* 구매 다이얼로그 */}
      {showPurchase && selectedProduct && (
        <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(45,43,43,.5)', zIndex: 70 }}>
          <div style={{ width: 'min(440px, 100%)', display: 'flex', flexDirection: 'column', gap: 12, padding: 16, background: COLORS.surface, boxShadow: '0 12px 32px rgba(45,43,43,.22)' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 400 }}>구매하기</div>
              <div style={{ flex: 1 }} />
              <button onClick={() => setShowPurchase(false)} style={{ width: 28, height: 28, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16 }}>✕</button>
            </div>
            <div style={{ fontSize: 14.5 }}>{selectedProduct.name}</div>
            {selectedProduct.has_options && (
              <select
                value={selectedOptionId ?? ''}
                onChange={e => setSelectedOptionId(e.target.value || undefined)}
                style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: `1px solid ${COLORS.divider}`, background: '#fff', color: COLORS.text }}
              >
                {(selectedProduct.options ?? []).length === 0 && <option value="">옵션 준비중</option>}
                {(selectedProduct.options ?? []).map(o => (
                  <option key={o.id} value={o.id} disabled={o.stock <= 0}>
                    {o.option_label} — {fmtWon(o.price)}{o.stock <= 0 ? ' (품절)' : ''}
                  </option>
                ))}
              </select>
            )}
            <hr style={{ height: 2, border: 0, margin: '16px 0', background: COLORS.divider }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 14 }}>수량</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <button onClick={() => setQuantity(q => Math.max(1, q - 1))} style={{ width: 36, height: 36, border: `1px solid ${COLORS.divider}`, background: 'transparent', cursor: 'pointer' }}>−</button>
                <div style={{ fontSize: 15, minWidth: 20, textAlign: 'center' }}>{quantity}</div>
                <button onClick={() => setQuantity(q => Math.min(99, q + 1))} style={{ width: 36, height: 36, border: `1px solid ${COLORS.divider}`, background: 'transparent', cursor: 'pointer' }}>＋</button>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 13.5, opacity: .6 }}>총 결제금액</div>
              <div style={{ fontSize: 19 }}>{fmtWon(total)}</div>
            </div>
            <button onClick={submitPurchase} disabled={submitting || (selectedProduct.has_options && !selectedOptionId)}
              style={{ justifyContent: 'center', height: 50, background: COLORS.accent, color: COLORS.bg, border: 'none', fontSize: 14, cursor: 'pointer', opacity: (submitting || (selectedProduct.has_options && !selectedOptionId)) ? .6 : 1 }}>
              {submitting ? '장바구니 담는 중...' : '구매하기'}
            </button>
          </div>
        </div>
      )}

      {embedSrc && <Script src="https://www.youtube.com/iframe_api" strategy="afterInteractive" />}
    </div>
  );
}
